import { ReactNode } from 'react';
import {
  components,
  MultiValueProps,
  InputProps as RSelectInputProps,
  OptionTypeBase,
} from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';

import { IconCloseSmall } from '@components/icons/close-small';
import { IconReturn } from '@components/icons/return';
import { useSelectTranslations } from '@components/select/locale';
import { theme } from '@styles/theme';

import {
  HelpText,
  FocusIconContainer,
  OptionContainer,
  InputBox,
  styles,
  StyledMultiValue,
} from './styles';

import { calculateTextWidth } from '../component-helpers';
import { FormLabel } from '../form-label';

interface SelectAsyncOption {
  label: string | ReactNode;
  value: string;
  helpText?: string;
}

export interface SelectMultiAsyncProps<T extends OptionTypeBase = OptionTypeBase>
  extends AsyncProps<T> {
  options: SelectAsyncOption[];
  defaultValue?: SelectAsyncOption | SelectAsyncOption[];
  onChange: (selectedOptions: SelectAsyncOption[]) => void;
  placeholder?: string;
  label: string;
  loadingMessage?: (obj: { inputValue: string }) => string | null;
  noOptions?: (obj: { inputValue: string }) => string | null;
}

const Option = (props: any) => {
  return (
    <components.Option {...props}>
      <FocusIconContainer>
        <OptionContainer $helpText={props.data.helpText}>
          {props.children}
          {props.data.helpText && <HelpText>{props.data.helpText}</HelpText>}
        </OptionContainer>
        {props.isFocused && <IconReturn color="purple50" />}
      </FocusIconContainer>
    </components.Option>
  );
};

export function SelectMultiAsync(props: SelectMultiAsyncProps) {
  const { t } = useSelectTranslations();
  const {
    options,
    defaultValue,
    onChange,
    placeholder = t('placeholder'),
    label,
    defaultOptions,
    loadOptions,
    loadingMessage,
    noOptions = () => t('no_options'),
  } = props;

  return (
    <div>
      <FormLabel text={label} />
      <AsyncSelect
        defaultValue={defaultValue}
        onChange={onChange}
        menuPlacement="auto"
        openMenuOnFocus
        noOptionsMessage={noOptions}
        menuPortalTarget={document.body}
        isMulti
        closeMenuOnSelect={false}
        styles={styles}
        isClearable={false}
        placeholder={placeholder}
        options={options}
        loadOptions={loadOptions}
        defaultOptions={defaultOptions}
        loadingMessage={loadingMessage}
        components={{
          Option,
          // Remove the dropdown indicator for multi
          DropdownIndicator: null,
          // Remove the clear indicator for multi
          ClearIndicator: null,
          LoadingIndicator: null,
          MultiValue,
          // Need custom input because the auto-grow built-in thing is broken (sigh)
          Input: CustomInput,
        }}
      />
    </div>
  );
}

function CustomInput(props: RSelectInputProps) {
  let {
    innerRef,
    cx,
    getStyles,
    theme: reactSelectTheme,
    isHidden,
    isDisabled,
    // @ts-ignore
    value,
    ...rest
  } = props;
  return (
    <InputBox
      // @ts-ignore stfu
      ref={innerRef}
      style={{
        width: calculateTextWidth(value, theme.font.regular16),
      }}
      value={value}
      {...rest}
    />
  );
}

function MultiValue({ children, removeProps, data }: MultiValueProps<any>) {
  return (
    <StyledMultiValue {...removeProps} $color={data.color ?? ''}>
      <span>{children}</span>
      <IconCloseSmall />
    </StyledMultiValue>
  );
}
